'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  Calendar,
  ChevronLeft,
  FileText,
  FolderOpen,
  // ScanText — only used by the OCR-confidence pill, commented out below.
  Sparkles
} from 'lucide-react';

import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { DOCUMENT_TYPES } from '../constants';
import { purifySearchSnippet } from '../utils';
import { SearchResultDetailDialog } from './search-result-detail-dialog';
import type { SearchHit, SearchScoring } from '../types';

interface SearchResultCardProps {
  hit: SearchHit;
  onSelect: (hit: SearchHit) => void;
  /**
   * The 0–100 relevance the ranking model gave this hit, from `hit.rank_score`
   * — the number that actually decided its position. PREFERRED by the ring.
   *
   * Absent whenever no such number exists: an unranked strategy, page 2+, a
   * candidate the model declined to score, or a page whose scores would not
   * render in descending order. The ring then falls back to
   * `similarityPercent` under a different label — never a dash, never 0%.
   */
  rankPercent?: number;
  /**
   * How close this hit's MEANING is to the query, 0–100, from
   * `hit.similarity`. Absent for a query that carried no embedding (a short
   * query, the keyword-only strategy).
   *
   * FALLBACK only. It is not the rank and does not explain the position, so
   * when it is what the ring shows, the ring says so — which is why the label
   * is rendered on screen rather than hidden in a tooltip.
   */
  similarityPercent?: number;
  /**
   * Query-level ranking configuration. Identical for every card on the page,
   * so it is threaded down rather than re-derived per card; the per-hit half of
   * the explanation lives on `hit.score_breakdown`.
   */
  scoring?: SearchScoring | null;
}

/**
 * One result row on the AI search page. Adapts to the entity type:
 *   - page_index: title + page X of Y + document date
 *   - ocr_result: container + matched OCR snippet + OCR-confidence pill
 *   - registration: container number + current stage
 * The metric column (right) carries ONE number, and WHICH number it is varies
 * per hit: the ranking model's relevance score when it rated this hit, the
 * semantic similarity when it did not, and nothing at all when neither exists.
 * The rendered label always names the one being shown — the two mean different
 * things and only the first explains the position.
 *
 * The RANK SCORE — the value that actually produced the position — is
 * deliberately NOT on the card. It lives in "Why this result?", where it can be
 * shown beside the factors that explain it. Two numbers in one corner is the
 * confusion this card has already shipped: a bare cosine read as the rank, then
 * a rank score that was null on most searches and printed `0%` down the page
 * when it was not. One labelled number, or none.
 *
 * The OCR pill only appears when the indexer emitted a confidence score.
 *
 * "Why this result?" (OMA-639) opens the detail dialog: the passages that
 * produced the match and a factor-by-factor account of the rate — both
 * read-only, and both rendered from payload the endpoint already returned for
 * this user. The card root is a div rather than a button precisely so that
 * trigger can be its own control: a button inside a button is invalid and makes
 * it unreachable by keyboard.
 */
export function SearchResultCard({
  hit,
  onSelect,
  rankPercent,
  similarityPercent,
  scoring
}: SearchResultCardProps) {
  const t = useTranslations('search');
  const [detailOpen, setDetailOpen] = useState(false);

  // WHICH number the ring shows, decided per hit rather than per page: a
  // reranked page and an unranked one sit in the same list. Rank first because
  // it is the one that produced the order; similarity only when the model
  // returned nothing for this hit; no ring at all when neither exists, since a
  // 0% would be a claim about a computation that never ran.
  const rateRing =
    rankPercent != null
      ? { percent: rankPercent, labelKey: 'rankLabel', hintKey: 'rankHint' }
      : similarityPercent != null
        ? {
            percent: similarityPercent,
            labelKey: 'similarityLabel',
            hintKey: 'similarityHint'
          }
        : null;

  const docTypeLabel =
    hit.document_type &&
    (DOCUMENT_TYPES as readonly string[]).includes(hit.document_type)
      ? t(`docType.${hit.document_type}`)
      : null;

  const entityTypeLabel = hit.entity_type
    ? t(`entityType.${hit.entity_type}`)
    : null;

  const isOcrHit = hit.entity_type === 'ocr_result';
  const isDocumentHit = hit.entity_type === 'page_index' || isOcrHit;
  const Icon = isDocumentHit ? FileText : FolderOpen;

  const title =
    hit.title ??
    (isOcrHit
      ? t('ocrResultTitle', {
          container: hit.container_number ?? hit.identifiers?.[0] ?? '—'
        })
      : (hit.identifiers?.[0] ?? '—'));

  const container = hit.container_number ?? hit.identifiers?.[0] ?? null;

  return (
    <div
      className={cn(
        'group hover:border-primary/50 hover:bg-accent/30 relative flex w-full flex-col gap-3 overflow-hidden rounded-xl border p-4 text-start transition-all',
        'focus-within:border-primary/50'
      )}
    >
      <button
        type='button'
        onClick={() => onSelect(hit)}
        className={cn(
          'flex w-full flex-col gap-3 text-start',
          'focus-visible:outline-primary rounded-md focus-visible:outline-2 focus-visible:outline-offset-2'
        )}
      >
        <div className='flex w-full items-start gap-3'>
          <div
            className={cn(
              'flex size-9 shrink-0 items-center justify-center rounded-md',
              isDocumentHit
                ? 'bg-muted text-muted-foreground'
                : 'bg-primary/10 text-primary'
            )}
          >
            <Icon className='size-4' />
          </div>

          <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
            <div className='flex flex-wrap items-center gap-1.5'>
              {docTypeLabel && (
                <Badge variant='secondary' className='font-medium'>
                  {docTypeLabel}
                </Badge>
              )}
              {!docTypeLabel && entityTypeLabel && (
                <Badge variant='outline'>{entityTypeLabel}</Badge>
              )}
              {hit.current_stage && (
                <Badge variant='outline' className='text-[10px]'>
                  {t.has(`stage.${hit.current_stage}`)
                    ? t(`stage.${hit.current_stage}`)
                    : hit.current_stage}
                </Badge>
              )}
            </div>

            <h3 className='truncate text-sm font-semibold'>{title}</h3>

            {(container || hit.document_number) && (
              <div className='text-muted-foreground flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-xs'>
                <FolderOpen className='size-3' />
                {container && <span className='font-mono'>{container}</span>}
                {container && hit.document_number && (
                  <ChevronLeft className='size-3 rtl:rotate-180' />
                )}
                {hit.document_number && (
                  <span className='font-mono'>{hit.document_number}</span>
                )}
              </div>
            )}
          </div>

          {/*
            The model's own relevance outranks the cosine here, because it is
            the number that produced the order the reader is looking at. The
            cosine ranks nothing, so a page showing it can read as mis-sorted
            (76% above 76% above 73%) — true, and useless. It stays as the
            fallback for hits the model never scored, under its own label.
          */}
          {rateRing && (
            <RateRing
              percent={rateRing.percent}
              label={t(rateRing.labelKey)}
              hint={t(rateRing.hintKey)}
            />
          )}
        </div>

        {hit.snippet && (
          <div className='bg-muted/40 w-full rounded-md border px-3 py-2'>
            <p
              className='text-foreground/80 line-clamp-2 text-xs leading-relaxed'
              // Typesense wraps the match in <mark>; everything around it is OCR
              // text from an uploaded document, so it is escaped before the tags
              // are re-admitted, then re-parsed by DOMPurify.
              dangerouslySetInnerHTML={{
                __html: purifySearchSnippet(hit.snippet)
              }}
            />
          </div>
        )}
      </button>

      <div className='text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]'>
        {/* HIDDEN on the card by product decision (2026-09-17). OCR confidence
            describes how sure the text-recognition engine was when it extracted
            this document — it is NOT a search-relevance number and takes no part
            in ordering, so on a results list it sits next to metrics that do and
            reads as one of them.

            Nothing else is removed: `hit.ocr_confidence` is still sent by the
            API, still surfaced in "Why this result?" (flagged there as a
            non-ranking factor), and `ocrConfidence` / `ocrConfidenceHint` are
            still in both locales. Restoring is uncommenting this block.

        {hit.ocr_confidence != null && (
          <span
            title={t('ocrConfidenceHint')}
            className='inline-flex items-center gap-1 rounded-md bg-emerald-500/10 px-2 py-0.5 font-medium text-emerald-700 dark:text-emerald-300'
          >
            <ScanText className='size-3' />
            {t('ocrConfidence', { value: Math.round(hit.ocr_confidence) })}
          </span>
        )}
        */}
        <span className='ms-auto inline-flex items-center gap-3'>
          {hit.page_number != null && (
            <span className='inline-flex items-center gap-1'>
              <FileText className='size-3' />
              {hit.total_pages != null
                ? t('pageLocation', {
                    page: hit.page_number,
                    total: hit.total_pages
                  })
                : t('pageLocationShort', { page: hit.page_number })}
            </span>
          )}
          {/* OCR hits: how many other pages also matched the query */}
          {isOcrHit &&
            hit.match_page_count != null &&
            hit.match_page_count > 1 && (
              <span className='bg-primary/10 text-primary inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-medium'>
                {t('additionalMatchedPages', {
                  count: hit.match_page_count - 1
                })}
              </span>
            )}
          {hit.document_date && (
            <span className='inline-flex items-center gap-1'>
              <Calendar className='size-3' />
              {hit.document_date}
            </span>
          )}
        </span>
      </div>

      <button
        type='button'
        onClick={() => setDetailOpen(true)}
        className={cn(
          'text-muted-foreground hover:text-foreground inline-flex w-fit items-center gap-1 rounded-md text-[11px] font-medium transition-colors',
          'focus-visible:outline-primary focus-visible:outline-2 focus-visible:outline-offset-2'
        )}
      >
        <Sparkles className='size-3.5' aria-hidden='true' />
        {t('explain.show')}
      </button>

      {/* Mounted only once opened: the evidence list is per-card and there can
          be 25 cards on a page. */}
      {detailOpen && (
        <SearchResultDetailDialog
          hit={hit}
          scoring={scoring}
          open={detailOpen}
          onOpenChange={setDetailOpen}
        />
      )}
    </div>
  );
}

/**
 * Compact circular meter for whichever rate the caller resolved — the ranking
 * model's relevance, or the semantic similarity when there is no rank. Stroke
 * ratio = percent/100; track is muted so the filled arc reads as foreground
 * emphasis even in dark themes.
 *
 * The label is RENDERED, not only a tooltip, and that is the whole reason this
 * number is allowed on the card at all. A BARE circle beside a list position is
 * read as the reason for that position no matter what the hover text says —
 * and when the ring falls back to the cosine, which orders nothing, an
 * unlabelled 69% above an unlabelled 66% gets reported as a sorting bug. A
 * tooltip did not fix it: it is unreachable on touch and invisible to anyone
 * scanning the page. Never demote this label back into `title` alone.
 */
function RateRing({
  percent,
  label,
  hint
}: {
  percent: number;
  label: string;
  hint: string;
}) {
  const safe = Math.max(0, Math.min(100, Math.round(percent)));
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - safe / 100);

  return (
    <div title={hint} className='flex shrink-0 flex-col items-center gap-1'>
      <div
        aria-label={`${label} ${safe}%`}
        className='relative flex size-10 items-center justify-center'
      >
        <svg
          className='size-10 -rotate-90'
          viewBox='0 0 40 40'
          aria-hidden='true'
        >
          <circle
            cx='20'
            cy='20'
            r={radius}
            strokeWidth='3'
            className='stroke-muted fill-none'
          />
          <circle
            cx='20'
            cy='20'
            r={radius}
            strokeWidth='3'
            strokeLinecap='round'
            className='stroke-primary fill-none transition-[stroke-dashoffset] duration-500'
            strokeDasharray={circumference}
            strokeDashoffset={offset}
          />
        </svg>
        <span className='text-primary absolute text-[10px] font-bold'>
          {safe}%
        </span>
      </div>
      <span className='text-muted-foreground max-w-20 text-center text-[9px] leading-tight font-medium'>
        {label}
      </span>
    </div>
  );
}
